import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { useWorkspaceStore } from '../../core/store/workspaceStore';
import { registerPendingBufferFlush } from '../../core/store/pendingBufferFlushes';
import { useAiLockStore } from '../../../terminal/store/aiLockStore';
import { toast } from '../../../../components/ui/toaster';
import type { QueryEditorHandle } from '../components/QueryEditor';

type UseQueryBufferSyncParams = {
  tabId: string;
  /** The tab's persisted query text from the workspace store. */
  storeQuery: string | undefined;
  isGeneratingScript: boolean;
  editorRef: RefObject<QueryEditorHandle | null>;
};

/**
 * The query-buffer state machine for a query editor tab. Monaco owns the live
 * content; this hook keeps the four representations in sync — `queryRef`
 * (synchronous truth), the debounced React `query` state, the workspace store,
 * and the Monaco model — across keystrokes (debounced flush), script
 * generation completing, external store writes (e.g. MCP ui_set_editor_text),
 * recent-query selection, and the AI-lock typing warning. Behavior carried
 * verbatim from the previous inline implementation.
 */
export function useQueryBufferSync({
  tabId,
  storeQuery,
  isGeneratingScript,
  editorRef,
}: UseQueryBufferSyncParams) {
  const updateTabContent = useWorkspaceStore((state) => state.updateTabContent);

  const [query, setQuery] = useState(() => storeQuery || '');
  // queryRef is the synchronous truth for the editor buffer. Monaco owns the
  // live content; the React `query` state lags by `flushQueryDebounced`'s
  // window (`setQuery` is no longer called per keystroke). All "I need the
  // current text right now" code paths (execute, save, file write, plsql
  // detection, AI chat) MUST read from `editorRef.current?.getValue()` (which
  // returns Monaco's truth) or `queryRef.current`, NOT `query`.
  const queryRef = useRef(query);

  // AI lock check - show warning if user types in a locked tab
  const isLockedByAi = useAiLockStore((s) => s.lockedTabs.has(tabId));
  const aiLockWarningShownRef = useRef(false);

  // Reset warning flag when lock status changes
  useEffect(() => {
    if (!isLockedByAi) {
      aiLockWarningShownRef.current = false;
    }
  }, [isLockedByAi]);

  // Track the query we last set to the store to detect external changes (e.g., MCP ui_set_editor_text)
  const lastStoredQueryRef = useRef(query);

  // Sync query when script generation completes
  // We use a ref to track previous isGeneratingScript to detect the transition
  const wasGeneratingRef = useRef(isGeneratingScript);
  useEffect(() => {
    // When transitioning from generating to not generating, sync the query
    if (wasGeneratingRef.current && !isGeneratingScript && storeQuery) {
      console.log('[QueryEditorTab] Script generation complete, syncing query');
      queryRef.current = storeQuery;
      lastStoredQueryRef.current = storeQuery;
      setQuery(storeQuery);
      // Monaco is uncontrolled — push the new content into the editor model
      // imperatively. The resulting onChange will land back in
      // handleQueryChange but be skipped by the debounced flush since
      // lastStoredQueryRef is already up to date.
      editorRef.current?.setValue(storeQuery);
    }
    wasGeneratingRef.current = isGeneratingScript;
  }, [isGeneratingScript, storeQuery, editorRef]);

  // Sync local query state when the store changes externally (e.g., MCP ui_set_editor_text)
  useEffect(() => {
    const nextStoreQuery = storeQuery ?? '';
    // If the store query differs from what we last set, it was changed externally
    if (nextStoreQuery !== lastStoredQueryRef.current && nextStoreQuery !== query) {
      queryRef.current = nextStoreQuery;
      lastStoredQueryRef.current = nextStoreQuery;
      setQuery(nextStoreQuery);
      editorRef.current?.setValue(nextStoreQuery);
    }
  }, [storeQuery, query, editorRef]);

  // Debounced flush of the editor buffer into React state + the workspace
  // store. Doing this synchronously per keystroke was the dominant cost on
  // the typing path (~6.5ms/key on M4 Max from the perf profile) — Monaco
  // already owns the live content, so React only needs the value periodically
  // for things that genuinely re-render (title, save dirty-check, etc).
  const flushQueryDebounced = useDebouncedCallback(() => {
    const current = queryRef.current;
    if (current === lastStoredQueryRef.current) return;
    lastStoredQueryRef.current = current;
    setQuery(current);
    updateTabContent(tabId, { query: current });
  }, 200);

  // Force-persist on unmount (tab close, session end). Without this, any
  // keystrokes inside the 200ms debounce window before unmount would never
  // make it into the store and would be lost on session restore.
  useEffect(() => {
    return () => {
      flushQueryDebounced.flush();
    };
  }, [flushQueryDebounced]);

  // Same for an immediate session save while the tab stays mounted — the
  // window closing or the app quitting (`saveSessionImmediately`): keystrokes
  // inside the 200 ms window must be in the store before the snapshot.
  useEffect(
    () => registerPendingBufferFlush(() => flushQueryDebounced.flush()),
    [flushQueryDebounced]
  );

  // Keystroke handler. Must NOT re-render this component synchronously —
  // updates a ref and schedules the debounced flush instead. The handler's
  // identity is stable across keystrokes (deps only change when AI lock
  // toggles), so QueryEditor's React.memo skips and Monaco never re-renders
  // through React on the typing path.
  const handleQueryChange = useCallback((newQuery: string) => {
    if (isLockedByAi && !aiLockWarningShownRef.current) {
      aiLockWarningShownRef.current = true;
      toast.warning({
        title: 'AI is editing this tab',
        description: 'Changes you make may be overwritten by the AI agent.',
      });
    }
    queryRef.current = newQuery;
    flushQueryDebounced();
  }, [isLockedByAi, flushQueryDebounced]);

  // Handle selecting a query from the recent queries dropdown
  const handleSelectRecentQuery = useCallback((sql: string) => {
    // Update the Monaco model immediately so a fast follow-up "Execute" runs the selected query,
    // not stale editor content (important for E2E stability and user experience).
    const didSetEditorValue = editorRef.current?.setValue(sql) ?? false;
    // Keep React state/ref/store in sync even when Monaco is updated imperatively.
    queryRef.current = sql;
    lastStoredQueryRef.current = sql;
    setQuery(sql);
    updateTabContent(tabId, { query: sql });

    if (didSetEditorValue) {
      editorRef.current?.focus();
    }
  }, [tabId, updateTabContent, editorRef]);

  return {
    query,
    setQuery,
    queryRef,
    lastStoredQueryRef,
    flushQueryDebounced,
    handleQueryChange,
    handleSelectRecentQuery,
  };
}
